import useDeleteConfirmDialog from 'common/components/useDeleteConfirmDialog'
import { CRUDAction, FeatureKey } from 'common/constants/permission'
import { useMutateRemoveCategory } from 'modules/inventory/api/mutation'
import { Category } from 'modules/inventory/api/type'
import CreateCategoryDialog from 'modules/inventory/pages/category/CreateCategoryPage'
import { useFarmGroup } from 'modules/member/api/selector'
import { useGetCRUDByFeature } from 'modules/user/api/selector'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'

interface Props {
  categoryId: string
  onClose: () => void
  open: boolean
  categoryItem: Category
  anchorEl: HTMLElement | null
}

const CategoryTools = ({ categoryId, onClose, open, categoryItem, anchorEl }: Props) => {
  const { t } = useTranslation(['category'])
  const cruds = useGetCRUDByFeature(FeatureKey.Inventory)
  const { refetch: refetchFarmGroup } = useFarmGroup('', { enabled: false })

  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | undefined>(undefined)

  const removeCategoryMutation = useMutateRemoveCategory()

  const { dialog, openDialog: openConfirmDeleteDialog } = useDeleteConfirmDialog({
    onConfirm: async () => {
      if (categoryId) {
        await removeCategoryMutation
          .mutateAsync(categoryId, { onSuccess: () => onClose() })
          .finally(() => refetchFarmGroup())
      }
    },
  })

  const handleEditCategory = () => {
    setEditingCategory(categoryItem)
    setEditDialogOpen(true)
    onClose()
  }

  const toolsMenuItems = [
    {
      icon: <img src="/static/icons/ci_edit.svg" alt="edit" />,
      title: t('category.edit'),
      crudIdx: CRUDAction.Update,
      onClick: handleEditCategory,
    },
    {
      icon: <img src="/static/icons/ci_delete.svg" alt="delete" />,
      title: t('category.delete'),
      crudIdx: CRUDAction.Delete,
      onClick: openConfirmDeleteDialog,
    },
  ]

  const getTools = () => {
    if (!cruds || !categoryItem) return []
    return toolsMenuItems.filter((m) => {
      if (m.crudIdx === CRUDAction.Delete && categoryItem.is_default) return false
      return m.crudIdx === undefined || cruds[m.crudIdx] === 1
    })
  }

  return (
    <Paper>
      <Menu
        sx={{ width: '240px' }}
        id={`edit-card-${categoryId}`}
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        MenuListProps={{ 'aria-labelledby': `basic-button-${categoryId}` }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            elevation: 6,
            variant: 'elevation',
            sx: { borderRadius: 2 },
          },
        }}
      >
        <MenuList sx={{ py: 0 }}>
          {getTools().map((menuItem, index) => (
            <MenuItem key={index} onClick={menuItem.onClick}>
              <ListItemIcon sx={{ mr: 0 }}>{menuItem.icon}</ListItemIcon>
              <ListItemText>
                <Typography variant="body2">{menuItem.title}</Typography>
              </ListItemText>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
      {dialog}
      <CreateCategoryDialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setEditingCategory(undefined) }}
        category={editingCategory}
      />
    </Paper>
  )
}

export default CategoryTools
